import { Directive, ElementRef, forwardRef, inject } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/** Propriedades do `vivest-autocomplete` (design system) usadas pela integração com formulários. */
type VivestAutocompleteElement = HTMLElement & {
  value: string;
  disabled: boolean;
  options: { value: string }[];
};

/**
 * Liga o `<vivest-autocomplete>` do design system aos formulários do Angular (`formControlName`,
 * `formControl` e `ngModel`). O web component dispara `change` ao escolher uma opção; enquanto o
 * usuário digita, o `value` dele é o texto digitado. O controle só recebe o valor de uma opção: o
 * texto que não corresponde a nenhuma (ou o campo apagado) vira `''`, ou seja, sem filtro.
 *
 * @example
 * <vivest-autocomplete label="Plano" [options]="opcoes" formControlName="plano"></vivest-autocomplete>
 */
@Directive({
  selector: 'vivest-autocomplete[formControlName], vivest-autocomplete[formControl], vivest-autocomplete[ngModel]',
  standalone: true,
  host: {
    '(change)': 'aoAlterar()',
    '(input)': 'aoAlterar()',
    '(focusout)': 'aoSair()',
  },
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => VivestAutocompleteValueAccessorDirective),
      multi: true,
    },
  ],
})
export class VivestAutocompleteValueAccessorDirective implements ControlValueAccessor {
  private readonly elemento = inject<ElementRef<VivestAutocompleteElement>>(ElementRef).nativeElement;

  private onChange: (valor: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  writeValue(valor: string | null): void {
    this.elemento.value = valor ?? '';
  }

  registerOnChange(fn: (valor: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(desabilitado: boolean): void {
    this.elemento.disabled = desabilitado;
  }

  aoAlterar(): void {
    const valor = this.elemento.value;
    this.onChange(this.elemento.options.some((opcao) => opcao.value === valor) ? valor : '');
  }

  aoSair(): void {
    this.onTouched();
  }
}
